import { Component, OnInit, OnDestroy, ViewChild, NgZone, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AnalyzerBannerComponent } from './element-components/analyzer-banner/analyzer-banner/analyzer-banner.component';
import { ChipsComponent } from './element-components/chips/chips.component';
import { BannerComponent } from './components/banner/banner.component';
import { TopNavigationComponent } from './components/top-navigation/top-navigation.component';
import { PanelNavigationComponent } from './components/panel-navigation/panel-navigation.component';
import { LoaderComponent } from './element-components/loader/loader.component';
import { ModalComponent } from './element-components/modal/modal.component';
import { QualifyingQuestionComponent } from './components/qualifying-question/qualifying-question.component';
import { IdleComponent } from './views/idle/idle.component';
import { Observable, Subscription, filter, fromEvent } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UserEventsTrackingService } from './services/user-events-tracking/user-events-tracking.service';
import { ScanSource } from './models/user-events.model';
import { NavigationHistoryService } from './services/navigation-history/navigation-history.service';
import { CookieStorageService } from './services/cookie-storage/cookie-storage.service';
import { UserAuthenticationService } from './services/user-authentication/user-authentication.service';
import { PreviewContextService } from './services/preview-context/preview-context.service';
import { SignalRService } from './services/signal-r/signal-r.service';
import { NotificationsService } from './services/notifications/notifications.service';
import type { IChipConfig } from './element-components/chips/chips.component';
import { InactivityCheckerService } from './services/inactivity-checker/inactivity-checker.service';
import { WorkspaceService } from './services/workspace/workspace.service';
import { UserService } from './services/user/user.service';
import { PortalService } from './services/portal/portal.service';
import { loadingModalObservable } from './models/portal.model';
import { ApplicationStateService } from './services/application-state/application-state.service';
import { SessionStorageKeyEnum, SessionStorageService } from './services/session-storage/session-storage.service';
import type { UserWorkspace } from './models/workspace.model';
import { UserWorkspaceStatus } from './models/workspace.model';
import { TenantAnalyzerService } from './services/tenant-analyzer/tenant-analyzer.service';
import { ITenantAnalyzerRequest } from './element-components/tenant-analyzer-modal/tenant-analyzer-modal.component';
import type { IAnalyzerBannerConfig } from './element-components/analyzer-banner/analyzer-banner/analyzer-banner.component';
import { AnalyzerType, OriginType } from './models/tenant-analzer/tenant-analzer.model';
import { isLocalHost, normalizeURLParams } from './models/util.model';
import { loadSVGSprite } from 'src/assets/svg-sprite';
import type { IModal } from './element-components/modal/modal.component';
import { SeoService } from './services/seo/seo.service';
import { CatalogProductType } from './models/portal.model';
import Intercom from '@intercom/messenger-js-sdk';
import type { UserAuthenticationStatus, UserInformation } from './models/profile.model';
import type { InitType } from '@intercom/messenger-js-sdk/dist/types';
import { SidebarNavigationComponent } from './components/sidebar-navigation/sidebar-navigation.component';
import { ConsentsPermissionsService } from './modules/workspace/services/consents-permissions/consents-permissions.service';

@Component({
	selector: 'bt-app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		RouterOutlet,
		AnalyzerBannerComponent,
		ChipsComponent,
		BannerComponent,
		SidebarNavigationComponent,
		TopNavigationComponent,
		PanelNavigationComponent,
		LoaderComponent,
		ModalComponent,
		QualifyingQuestionComponent,
		IdleComponent,
	]
})
export class AppComponent implements OnInit, OnDestroy {
	@ViewChild(SidebarNavigationComponent) sidebarNavigationComponent: SidebarNavigationComponent | undefined; // This component is undefined in the public catalog

	onlineEvent: Observable<Event> = new Observable<Event>();
	offlineEvent: Observable<Event> = new Observable<Event>();

	public fullPageScreen = false;
	public isPublicPage = false;

	subscriptions: Subscription[] = [];

	connectionStatusMessage = "";
	connectionStatus = "";
	isOffline = false;
	userWorkspace: UserWorkspace | undefined;

	// IDLE STATE
	public idleObs$!: Subscription;
	public isIdle = false;

	// UI Flags helpers
	public appCanBeDisplayed = false;
	public isTeamsAppCatalog = location.pathname.startsWith("/catalog/tenant") && location.search.toLowerCase().indexOf("source=teamsapp") !== -1;
	public hideTopNagivation = false;
	public hidePanelNagivation = false;
	public get isInPreviewUser(): boolean {
		return this.previewContext.isPreviewUser;
	}
	public previewUserEmail: string | undefined;

	public chip: IChipConfig | undefined;
	public analyzerBanner!: IAnalyzerBannerConfig | undefined;

	// SP CATALOG
	public isSelfServiceSpCatalog = location.pathname.startsWith("/spcatalog");

	public loadingModal: IModal | undefined
	public showQualifyingQuestion = false

	public readonly navigationHistory = inject(NavigationHistoryService);
	public readonly cookieStorage = inject(CookieStorageService);
	public readonly activatedRoute = inject(ActivatedRoute);
	private readonly userTrackingService = inject(UserEventsTrackingService);
	private readonly router = inject(Router);
	private readonly userAuthenticationService = inject(UserAuthenticationService);
	private readonly previewContext = inject(PreviewContextService);
	private readonly signalRService = inject(SignalRService);
	private readonly notificationsService = inject(NotificationsService);
	private readonly idleChecker = inject(InactivityCheckerService);
	private readonly workspaceService = inject(WorkspaceService);
	private readonly consentsPermissionsService = inject(ConsentsPermissionsService);
	private readonly userService = inject(UserService);
	private readonly portalService = inject(PortalService);
	private readonly sessionStorage = inject(SessionStorageService);
	private readonly applicationStateService = inject(ApplicationStateService);
	private readonly tenantAnalyzerService = inject(TenantAnalyzerService);
	private readonly seoService = inject(SeoService);
	private readonly ngZone = inject(NgZone);

	constructor() {
		this.subscriptions.push(this.idleChecker.listenForUserActivity(() => this.resetTimer()));
		if (environment.production) { window.console.log = () => { /* This remove all the console log when we are at production */ }; }

		if (this.isSelfServiceSpCatalog) {
			// Request token from sharepoint
			this.requestNewTokenFromSharepoint()

			// Init event listener to receive token
			window.addEventListener('message', (event) => {
				this.subscribeMessageIframeSPCatalog(event);
			});

			// setInterval to get a new token when 1h passes
			setInterval(() => {
				this.requestNewTokenFromSharepoint()
			}, 60 * 60 * 1000)
		}

		if (this.isTeamsAppCatalog) {
			window.addEventListener('message', (event) => {
				this.subscribeMessageIframeTeamsApp(event);
			});
		}

		// Load the Icons SVG Sprite to the HTML DOM
		loadSVGSprite();

		if (!this.isSelfServiceSpCatalog) {
			// Start the route listening for the current component
			//! IMPORTANT: This component cannot be hide NEVER on this application or the navigation does not work as expected
			this.navigationHistory.startSaveHistory();

			// Initialize needed subscribers to track user events
			this.userTrackingService.InitializeSubscribers();

			// Instance the Connection Lost Handler
			this.createEventConnectionLost();

			//Add subscription for pop up that shows that user dont have licenses.
			this.subscriptions.push(loadingModalObservable.subscribe((message) => { this.loadingModal = message ? {
				id: 'app_loading', footer: false, header: false, isProcessing: true, processingLabel: message, title: "" } : undefined }));
		}
	}

	public async ngOnInit() {
		if (!this.isSelfServiceSpCatalog) {
			// Hide the navigation bars on the template creation wizard
			this.handleRouting();

			const canAccess = await this.checkAuthorizationSecret();

			if (canAccess) {
				// Load the portal settings from the API
				this.portalService.loadPortalSettings();

				this.isPublicPage = location.pathname.startsWith("/catalog/") || location.pathname.startsWith("/tenant/") || location.pathname.startsWith("/approval")

				if (this.isPublicPage) {
					this.fullPageScreen = true;
					this.appCanBeDisplayed = true;
				}
				else {
					await this.initApp();
				}
			}
			else {
				this.appCanBeDisplayed = true; // Remove loading and show the unauthorized page
			}
		} else {
			const canAccess = await this.checkAuthorizationSecret();
			if (!canAccess) {
				// TODO: SOMETHING HERE
			}
		}
	}

	public ngOnDestroy(): void {
		// Destroy all the subscriptions
		this.subscriptions.forEach(subscription => { if (subscription != null) { subscription.unsubscribe() } });
		this.idleObs$.unsubscribe();
	}

	private async initApp() {
		// Check if user is authenticated
		await this.userAuthenticationService.getAuthenticatedUser().then(async user => {

			if (user.isAuthenticated) {

				//Validate if is bindtuning admin and if is in preview mode
				const normalizedParams = normalizeURLParams(this.activatedRoute.snapshot.queryParams)
				const previewUserId = normalizedParams['userid'];
				const workspaceId = normalizedParams['workspaceid'];

				// Set Preview User if is needed
				if (previewUserId) {
					await this.initializePreviewUser(previewUserId, normalizedParams);
				}

				// Profile data is required by the app even when Intercom and reminders are disabled.
				const profile = await this.userService.getUserProfile();
				await this.initIntercomMessenger(user, profile);

				//is the user first login in the app? true/false
				const remindMeLaterKey = this.sessionStorage.get(this.sessionStorage.getSessionStorageKey(SessionStorageKeyEnum.QualifyingQuestionRemindMeLater));
				if (remindMeLaterKey !== true) {
					if (!profile?.qualifyingQuestion) {
						this.showQualifyingQuestion = true;
					}
				}

				// Get the user Workspaces to allow app to run
				// Fallback to localStorage if no workspaceId in URL params
				await this.workspaceService.getUserWorkspace(this.previewUserEmail ? workspaceId ?? "default" : undefined);

				// Get the user Consents & Permissions associated to allow app to run
				this.consentsPermissionsService.getUserConsentsPermissions();

				// ! TAKE CARE ADDING REQUEST'S ON TOP OF THIS LINE
				// ! WE WILL ONLY NEED TO HAVE ON TOP OF THIS THE NECESSARY REQUEST TO ALLOW THE APP TO DO THE FIRST PAINT FAST
				// Allow app to be displayed
				this.appCanBeDisplayed = true;

				this.userWorkspace = this.workspaceService.getCurrentUserWorkspace();
				if (this.userWorkspace?.id === workspaceId) {
					delete normalizedParams["workspaceid"]
					this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: normalizedParams })
				}

				if (this.userWorkspace && this.userWorkspace.status.toLowerCase() == UserWorkspaceStatus.Deleted) {
					this.router.navigate([{ outlets: { modal: 'deleted-workspace-confim-pop-up' } }], { skipLocationChange: true });
				}

				if (this.workspaceService.canViewPulse()) {
					this.tenantAnalyzerService.getTenantAnalyzerStatus().then(tenantScan => {
						if (tenantScan && tenantScan.isRunning && tenantScan.isRequestedByUser && tenantScan.isRunning.analyzerType === AnalyzerType.Full) {
							this.tenantAnalyzerService.showBannerAndUpgradeProgress();
						}
					})
				}

				this.validateExternalLoginNotifications()

				this.validateAndExecuteExternalActions();

				// Start the idle count time to inactivity
				this.createIdleInactivity();

				const workspace = this.workspaceService.getCurrentUserWorkspace();
				if (workspace) {
					this.signalRService.startAdminAppConnection(workspace.id, workspace.info.tenantId);

					// Initialize the signalR Listeners
					// POSSIBLE IMPROVEMENT: MOVE THIS TO ADD LISTENER ONLY WHEN NEEDED 
					// FOR EXAMPLE ONLY WHEN OPENING CERTAIN AREAS OF APP WE START THE LISTENER
					this.signalRService.addTokensUpdateListener()
					this.signalRService.addIntegrityScoreListener()
					this.signalRService.addDataAnalysisListener()
					this.signalRService.addWorkflowsListener()
					this.signalRService.addWorkflowExecutionStatusListener()
					this.signalRService.addAutomationsListener()
					this.signalRService.addTenantInformationListener()
					this.signalRService.addInstallListener()
					this.signalRService.addApplyListener()
					this.signalRService.addDownloadListener()
					this.signalRService.addGenerateListener()
					this.signalRService.addUpdateListener()
					this.signalRService.addInstalledInfoListener()
					this.signalRService.addConnectionsListener()
					this.signalRService.addCheckedOutEntitiesListener()
					this.signalRService.addTemplatesUpdatesListener()
					this.signalRService.addPulseActionListener()
					if (this.workspaceService.canAnalyzeTenant()) {
						this.signalRService.addTenantAnalyzerListener()
					}
					this.signalRService.addWorkspaceTokensUpdatedListener()
				}

				this.applicationStateService.getApplicationStateSelector().subscribe(x => {
					this.chip = x.chipActive;
					this.analyzerBanner = x.analyzerBanner;
				});
			}
			else {
				await this.initIntercomMessenger(user);
				this.appCanBeDisplayed = true;
			}

		});
	}

	private validateAndExecuteExternalActions() {
		const action: string | undefined = this.activatedRoute.snapshot.queryParams["action"]
		if (action) {
			const status: string | undefined = this.activatedRoute.snapshot.queryParams["status"]
			// is not null or undefined
			if (status) {
				// contains success in the status
				if (status.toLowerCase().indexOf("success") !== -1) {
					// execute the action
					this.externalActionRequests[action.toLowerCase()]();
				}
			}
			else {
				// execute the action if there is no status to validate
				this.externalActionRequests[action.toLowerCase()]();
			}
		}
	}

	private readonly externalActionRequests: { [key: string]: () => void } = {
		startpulsescan: () => this.startTenantAnalyzer(OriginType.Pulse365, 'pulse'),
		startintranetscan: () => this.startTenantAnalyzer(OriginType.Intranet, 'intranet'),
		startintegrityscan: () => this.startTenantAnalyzer(OriginType.Pulse365, 'integrity_score')
	}

	private validateExternalLoginNotifications() {
		const status: string | undefined = this.activatedRoute.snapshot.queryParams["status"]
		if (status && typeof status === "string" && this.externalLoginNotifications[status.toLowerCase()]) {
			this.externalLoginNotifications[status.toLowerCase()]()

			const normalizedParams = normalizeURLParams(this.activatedRoute.snapshot.queryParams)
			delete normalizedParams["status"]
			this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: normalizedParams }) // Remove query params
		}
	}

	private readonly externalLoginNotifications: { [key: string]: () => void } = {
		error: () => this.notificationsService.showError(this.activatedRoute.snapshot.queryParams["message"], "Error occurred", -1),
		upsertauthenticationsuccess: () => this.notificationsService.showSuccess("Authentication successful"),
		createsuccess: () => this.notificationsService.showSuccess("Connection created sucessfully! You can now start using it in your automations.", undefined, 10000),
		createerror: () => this.notificationsService.showError(this.activatedRoute.snapshot.queryParams["error"], "There was an error creating the connection. Please try again", -1),
		createconflict: () => this.notificationsService.showError(this.activatedRoute.snapshot.queryParams["error"], "The email you connected to is already being used. The connection was not created", -1),
		reconnectsuccess: () => this.notificationsService.showSuccess("Reconnected sucessfully!", undefined, 10000),
		reconnecterror: () => this.notificationsService.showError(this.activatedRoute.snapshot.queryParams["error"], "There was an error reconnecting. Please try again", -1),
		switchaccountsuccess: () => this.notificationsService.showSuccess("Account switched sucessfully!", undefined, 10000),
		switchaccounterror: () => this.notificationsService.showError(this.activatedRoute.snapshot.queryParams["error"], "There was an error switching account. Please try again", -1),
		switchaccountconflict: () => this.notificationsService.showError(this.activatedRoute.snapshot.queryParams["error"], "The email you switched to is already being used. The connection was not created", -1),
	}

	// Hide the top navigation bar and the panel navigation bar, based on the current route
	// This is used to hide the navigation bars on the template creation wizard
	private handleRouting(): void {

		this.router.events.pipe(filter(event => event instanceof NavigationEnd)
		).subscribe(() => {
			let route = this.activatedRoute.firstChild;
			while (route) {

				// SEO
				this.seoService.generateCanonicalURL(route.snapshot.url);

				const pageId: string = route.snapshot.data["seoPageId"];
				const productId: string = route.snapshot.params["productId"];

				if (productId) {
					const catalogType: CatalogProductType = route.snapshot.data['catalogType'];
					this.seoService.generateSingleProdPageMetaTags(catalogType, productId);
				} else if (pageId) {
					this.seoService.getStaticPageMetaTags(pageId);
				} else {
					// DELETE
					this.seoService.deletePageMetaTags();
				}


				// Show full page screen in the case of needs to remove all the other static elements
				if (route.snapshot.data['fullPage']) {
					this.fullPageScreen = true;
					this.sidebarNavigationComponent?.hideSidebar();
					if (!route.snapshot.data['fullPageNoBodyClass']) {
						document.body.classList.add("bt-fullpage-mode");
					}
					return;
				}
				else {
					this.fullPageScreen = false;

					if (route.snapshot.data['hideAllNavigation']) {
						const intervalId = setInterval(() => {
							if (this.sidebarNavigationComponent) {
								this.sidebarNavigationComponent.hideSidebar();
								clearInterval(intervalId);
							}
						}, 100);
						this.hidePanelNagivation = true;
						this.hideTopNagivation = true;
						return;
					}

					if (route.snapshot.data['hidePanelNavigation']) {
						this.hidePanelNagivation = true;
						return;
					}

					if (route.snapshot.data['pageId'] === 'automation-run') {
						const normalizedParams = normalizeURLParams(this.activatedRoute.snapshot.queryParams)
						const workspaceId = normalizedParams['workspaceid']
						if (workspaceId) {
							this.hidePanelNagivation = true;
							this.hideTopNagivation = true;
							this.fullPageScreen = true;
						}
					}

					if (route.firstChild) {
						route = route.firstChild;
					} else {
						route = null;
						this.hideTopNagivation = false;
						this.hidePanelNagivation = false;
						const intervalShowId = setInterval(() => {
							if (this.sidebarNavigationComponent) {
								this.sidebarNavigationComponent.showSidebar();
								clearInterval(intervalShowId);
							}
						}, 100);
					}
				}
			}
			return;
		});
	}

	public resetTimer() {
		if (!this.isIdle) {
			this.idleChecker.notifyUserAction();
		}
	}

	// Reload Page
	public reload() {
		location.reload();
	}

	/** Create the listeners to check the internet lost connection */
	private createEventConnectionLost() {
		// Listen for changes of the window status to online
		this.subscriptions.push(fromEvent(window, 'online').subscribe(() => {
			this.connectionStatusMessage = 'Reconnecting... <br> Attempting to reconnect to the server. <br> Please wait.'
			this.connectionStatus = 'Looks like you\'re back online';
			this.isOffline = false;

			setTimeout(() => {
				document.getElementById("modal-no-internet-connection")?.classList.add('hidden');
			}, 3000);

		}));

		// Listen for changes of the window status to offline
		this.subscriptions.push(fromEvent(window, 'offline').subscribe(() => {
			document.getElementById("modal-no-internet-connection")?.classList.remove('hidden');
			this.connectionStatusMessage = 'Make sure you\'re connected to Wi - Fi and we\'ll get you back on track in no time.';
			this.connectionStatus = 'Looks like you\'re offline!';
			this.isOffline = true;
		}));
	}

	/** Create inactivity idle  */
	private createIdleInactivity() {
		// START IDLE TIMER
		this.idleChecker.startTimer();
		this.idleObs$ = this.idleChecker._userActionOccured$.subscribe(() => {
			this.ngZone.run(() => { this.isIdle = true; });
		})
	}

	private async initIntercomMessenger(user: UserAuthenticationStatus, userProfile?: UserInformation) {
		if (!isLocalHost() && environment.name !== "dev" && !this.isInPreviewUser) {
			let intercomSettings: InitType = { app_id: 'ehz0fnq3', region: 'eu' }
			if (user.isAuthenticated) {
				const intercomToken = await this.userService.getIntercomToken();

				const name = userProfile ? (userProfile.lastName ? userProfile.firstName + " " + userProfile.lastName : userProfile.firstName) : undefined;

				intercomSettings = {
					...intercomSettings,
					intercom_user_jwt: intercomToken,
					user_id: user.userId,
					name,
					phone: userProfile?.phone,
					avatar: userProfile?.photo ? {
						type: 'avatar',
						image_url: userProfile?.photo.url
					} : undefined
				}
			}


			Intercom(intercomSettings);
		}
	}



	public async startTenantAnalyzer(origin: OriginType, scanSource?: ScanSource) {
		const request: ITenantAnalyzerRequest = new ITenantAnalyzerRequest(AnalyzerType.Full, origin);
		await this.tenantAnalyzerService.validateAndStartTenantAnalyzer(request, false, origin, undefined, scanSource)
	}

	public subscribeMessageIframeSPCatalog(event: MessageEvent<unknown>) {
		if (event.origin && event.origin.endsWith(".sharepoint.com") && event.data && (event.data as Record<string, unknown>)['selfservice'] && (event.data as Record<string, unknown>)['selfservice'] === "spcatalog" && (event.data as Record<string, unknown>)['token']) {
			for (const [key, value] of Object.entries((event.data as Record<string, unknown>)['spTheme'] as Record<string, string>)) {
				document.documentElement.style.setProperty(`--${key}`, (value as string))
			}
			document.documentElement.style.setProperty("font-family", 'var(--mediumFontFamily)')
			document.documentElement.style.setProperty("-webkit-font-smoothing", 'antialiased')
			document.documentElement.style.setProperty("--fontWeightRegular", '400')
			document.documentElement.style.setProperty("--fontWeightSemibold", '600')
			document.documentElement.style.setProperty("--fontSizeBase300", '14px')
			document.documentElement.style.setProperty("--lineHeightBase300", '20px')
			document.documentElement.style.setProperty("--durationFaster", '100ms')
			document.documentElement.style.setProperty("--curveEasyEase", 'cubic-bezier(0.33,0,0.67,1)')
			document.documentElement.style.setProperty("--colorStrokeFocus2", '#000000')
			document.documentElement.style.setProperty("--colorNeutralStroke1", '#d2d0ce')
			document.documentElement.style.setProperty("--colorTransparentStroke", 'transparent')
			document.documentElement.style.setProperty("--strokeWidthThin", '1px')
			document.documentElement.style.setProperty("--strokeWidthThick", '2px')
			document.documentElement.style.setProperty("--borderRadiusMedium", '4px')
			document.documentElement.style.setProperty("--shadow2", '0 0 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.14)')
			document.documentElement.style.setProperty("--colorNeutralForegroundOnBrand", '#ffffff')
			document.documentElement.style.setProperty("--borderRadiusNone", '0')

			// Detect dark theme from the injected bodyBackground CSS variable and
			// add the 'sp-dark' class to <html> so SCSS can scope dark-mode overrides.
			const themeData = (event.data as Record<string, unknown>)['spTheme'] as Record<string, string>;
			const bodyBg = themeData['bodyBackground'];
			if (bodyBg) {
				document.documentElement.classList.toggle('sp-dark', this.isSpThemeDark(bodyBg));
			}

			this.userAuthenticationService.setPublicCatalogUserAuthenticatedUser((event.data as Record<string, unknown>)['token'] as string, "spcatalog")
		}
	}

	/** Returns true when the given hex colour is perceived as dark (luminance < 128). */
	private isSpThemeDark(hexColor: string): boolean {
		const hex = hexColor.replace('#', '');
		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);
		return (0.299 * r + 0.587 * g + 0.114 * b) < 128;
	}

	private requestNewTokenFromSharepoint() {
		const message = {
			"action": "generate-token",
			"request": "bt-selfservice-sp",
			"route": "token-request"
		}
		window?.top?.postMessage(message, "*");
	}

	public subscribeMessageIframeTeamsApp(event: MessageEvent<unknown>) {
		if (event.origin && event.origin.indexOf("//localhost:53000") !== -1 || event.origin === "https://btteamsapps.z14.web.core.windows.net" || event.origin === "https://teamsapps.bindtuning.com") {
			this.sessionStorage.save(SessionStorageKeyEnum.TeamsAppMessageContent, event.data);
		} else {
			return;
		}
	}

	private async checkAuthorizationSecret(): Promise<boolean> {
		// SECRET VALIDATION
		const appConnectionCookieName = environment.appConnectionCookie?.name;
		let returnValue = true;

		if (appConnectionCookieName) {
			const authCookie = this.cookieStorage.getCookie(appConnectionCookieName);

			if (!authCookie || authCookie !== environment.appConnectionCookie?.secret) {
				// Wrap the router event handling in a Promise to ensure it waits for completion
				returnValue = await new Promise<boolean>((resolve) => {
					const sub = this.router.events.subscribe(async (e) => {
						if (e instanceof NavigationEnd) {
							sub.unsubscribe();

							// Check if the query parameter contains the secret
							if (this.activatedRoute.snapshot.queryParams && this.activatedRoute.snapshot.queryParams["secret"] && this.activatedRoute.snapshot.queryParams["secret"] === environment.appConnectionCookie?.secret) {
								this.cookieStorage.setCookie(appConnectionCookieName, this.activatedRoute.snapshot.queryParams["secret"], 2);
								this.router.navigate([], {
									relativeTo: this.activatedRoute,
									queryParams: { secret: null },
									queryParamsHandling: 'merge',
								});
								resolve(true);
							} else {
								this.router.navigate(["unauthorized"]);
								resolve(false);
							}
						}
					});
				});
			}
		}

		return returnValue;
	}

	private async initializePreviewUser(previewUserId: string, normalizedParams: Record<string, string>): Promise<void> {
		this.previewUserEmail = undefined;
		if (!this.userAuthenticationService.isBindTuningAdmin() ||
			previewUserId === this.userAuthenticationService.getAuthenticatedUserId()) {
			this.previewContext.setPreviewUserId(undefined);
			return;
		}

		// HTTP requests and telemetry must see the same identity during profile validation.
		this.previewContext.setPreviewUserId(previewUserId);
		try {
			const profile = await this.userService.getUserProfile();
			if (profile) {
				this.previewUserEmail = profile.email;
				delete normalizedParams['userid'];
				this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: normalizedParams });
				return;
			}
		} catch {
			// A failed lookup must not leave the application impersonating the requested user.
		}
		this.previewContext.setPreviewUserId(undefined);
		this.notificationsService.showError('User not found.');
	}
}
